import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { toast } from "react-toastify";

import {
  getChoices,
  getNotQuiteCopy,
  getPositiveCopy,
  BRAND_VIOLET,
  usePrefersReducedMotion,
  vibrate,
  useImageBand,
  CONTEXT_BAND_MIN,
  CONTEXT_BAND_MAX,
} from "./cardCommon";
import { CardExpandSheet } from "./CardExpandSheet";
import { useFitToHeight } from "./useFitToHeight";

const POST_ANSWER_AUTO_ADVANCE_MS = 2000;

/**
 * Trivia card — quick multiple-choice question with right/wrong feedback.
 * Distinct from the standalone Quiz block (graded, retakes, pass threshold);
 * trivia cards are lightweight, single-shot checks for understanding inside
 * a swipe deck.
 *
 * Server-side scoring: when the answer is submitted, the BE recomputes
 * `is_correct` from `content.choices` and ignores client claims, so the
 * value we receive in the response is authoritative. We use whatever the
 * BE returns — never trust the client-side guess.
 *
 * Wrong-answer reveal: post-submit, the BE response payload includes
 * `correct_indices` + `explanation` (still stripped from the pre-submit
 * card content via _strip_quiz_answer_keys). When the user gets the
 * answer wrong, the correct choice gets flagged green and the
 * explanation block renders below — completing the learning loop.
 *
 * (Note: the stored `card_type` value is still `"quiz"` internally — the
 * rename is for code+UI readability, not a DB migration.)
 */
export function TriviaCard({
  card,
  isActive,
  shouldLoadMedia = true,
  existingResponse,
  onAdvance,
  onSubmitResponse,
  language,
  // When false (storyboard preview tiles), the card is display-only: choices
  // can't be selected and Submit is hidden. The preview marks the subtree
  // inert/aria-hidden, but that isn't reliable, so we gate behavior here too.
  interactive = true,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const content = card.content || {};
  const question = content.question || "";
  const choices = getChoices(card);
  const questionImageUrl = shouldLoadMedia ? card.media?.url || null : null;
  // Aspect-driven image band (40-55%), matching the split card but capped
  // lower to leave room for the question + answers + Submit below.
  const { band, loaded, imgRef, handleLoad } = useImageBand(
    questionImageUrl,
    CONTEXT_BAND_MIN,
    CONTEXT_BAND_MAX,
  );

  // Post-submit reveal data: the BE strips `is_correct` and `explanation`
  // from card content pre-submit (so employees can't peek), then
  // returns `correct_indices` + `explanation` on the response payload.
  // Fall back to `content.explanation` so preview mode (no stripping)
  // still works even if the parent didn't synthesize the response.
  const revealedCorrectIndices = React.useMemo(() => {
    const arr = existingResponse?.correct_indices;
    return Array.isArray(arr) ? new Set(arr) : null;
  }, [existingResponse]);
  const revealedExplanation =
    existingResponse?.explanation || content.explanation || "";

  // The post-answer explanation is the one unbounded, non-interactive piece
  // of the card, so it gets the "… more" treatment (clamp + expand sheet)
  // rather than being allowed to grow the card and force a clip. The
  // interactive content above (question + choices + Submit) is length-capped
  // and the image band yields height to it, so it never needs to scroll.
  const explanationSheetRef = React.useRef(null);
  const explanationObserverRef = React.useRef(null);
  const [explanationOverflowing, setExplanationOverflowing] =
    React.useState(false);
  const [explanationExpanded, setExplanationExpanded] = React.useState(false);
  // Callback ref rather than useRef + effect: the explanation only mounts
  // after submit (and lives inside AnimatePresence), so a dependency-array
  // effect wouldn't reliably re-measure on mount. The callback fires on
  // mount/unmount, and a ResizeObserver catches later size changes.
  const explanationRef = React.useCallback((node) => {
    if (explanationObserverRef.current) {
      explanationObserverRef.current.disconnect();
      explanationObserverRef.current = null;
    }
    if (!node) {
      setExplanationOverflowing(false);
      return;
    }
    const measure = () =>
      setExplanationOverflowing(node.scrollHeight - node.clientHeight > 4);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      explanationObserverRef.current = ro;
    }
  }, []);
  // Collapse the sheet when this card is no longer active, and focus the
  // sheet's scroll region on open so Arrow keys scroll it.
  React.useEffect(() => {
    if (!isActive) setExplanationExpanded(false);
  }, [isActive]);
  React.useEffect(() => {
    if (explanationExpanded && explanationSheetRef.current) {
      explanationSheetRef.current.focus({ preventScroll: true });
    }
  }, [explanationExpanded]);
  const openExplanation = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setExplanationExpanded(true);
  };

  // Pre-fill if the user already answered. We only know the *selection* —
  // not which is correct — until we submit (or had submitted previously).
  //
  // Validate restored indices against the current choice count.
  // Responses persist across deck edits, so an admin who removed or
  // reordered choices can leave a stored `selected_indices` value that
  // points beyond the current array. Without normalization, the card
  // would initialize as submitted (locked, auto-advance armed) but no
  // current chip would render as selected — leaving the learner unable
  // to re-answer with stale `is_correct` carried over. Drop any out-
  // of-range indices and only treat the card as submitted when at
  // least one valid index survives.
  const rawInitialSelectedIndices =
    existingResponse?.selected_indices || [];
  const initialSelectedIndices = (Array.isArray(rawInitialSelectedIndices)
    ? rawInitialSelectedIndices
    : []
  ).filter(
    (idx) =>
      typeof idx === "number" && idx >= 0 && idx < choices.length,
  );
  const initialIsCorrect = existingResponse?.is_correct ?? null;

  const [selectedIndices, setSelectedIndices] = React.useState(
    new Set(initialSelectedIndices),
  );
  const [submitted, setSubmitted] = React.useState(
    initialSelectedIndices.length > 0,
  );
  const [isCorrect, setIsCorrect] = React.useState(
    initialSelectedIndices.length > 0 ? initialIsCorrect : null,
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [shake, setShake] = React.useState(false);
  const [feedbackCopy, setFeedbackCopy] = React.useState(null);

  // Track the shake-reset timer so we can clear it if the card unmounts
  // mid-animation (e.g., user closes the player). Without this, React
  // logs an "update on unmounted component" warning when the timeout
  // fires after teardown.
  const shakeTimerRef = React.useRef(null);
  React.useEffect(() => {
    return () => {
      if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    };
  }, []);

  // Multi-correct detection. The BE serializer adds a `multi_select`
  // flag to stripped employee payloads (computed from how many choices
  // are flagged correct), so the player knows whether to allow more
  // than one selection without seeing the answer keys themselves. In
  // preview mode the answer keys aren't stripped, so we can also derive
  // it directly from the choices — falling back to that count keeps the
  // editor preview accurate even if a deck was authored before the BE
  // started emitting `multi_select`.
  const isMultiSelect =
    content.multi_select === true ||
    choices.filter((c) => c && c.is_correct).length > 1;

  // Choice display order. Default to shuffled (anti-pattern-memorization
  // — admins can opt out via `shuffle_choices: false` on the card). The
  // shuffle is computed once per card mount (via useMemo keyed on the
  // card's stable identity) so the order doesn't reshuffle on every
  // render. `displayOrder[displayIdx] = originalIdx` — we render in
  // shuffled order but still store / submit / score against original
  // indices so the BE's set-equality grading stays correct.
  const shuffleChoices = content.shuffle_choices !== false;
  const displayOrder = React.useMemo(() => {
    const indices = Array.from({ length: choices.length }, (_, i) => i);
    if (!shuffleChoices) return indices;
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.uuid, shuffleChoices, choices.length]);

  // Shrink the question + answers to fit the card height when a dense card would
  // otherwise clip the lower choices or Submit (the player can't scroll). Only
  // engages on the rare overflowing card; short cards stay at scale 1. Always on
  // — including the editor preview, which renders this real card at full player
  // size via ScaledCardPreview (then transform-scales the tile), so the fit is
  // computed on player dimensions and matches the live player exactly. Re-fits
  // when answered state changes, or the image mounts/band changes.
  //
  // questionImageUrl is a dep (not just band): media loads lazily as a slide
  // nears selection, so the band can MOUNT after the first fit. For common
  // landscape images the band sits at CONTEXT_BAND_MIN before and after, so
  // `band` alone never changes — without the URL dep a dense card would stay
  // fit-at-full-height and clip under the newly added image.
  const { cardRef, contentRef } = useFitToHeight({
    deps: [submitted, band, questionImageUrl, question, choices.length],
  });

  // After-answer auto-advance. Give extra time when there's an explanation
  // to read — 2s is barely enough to register feedback, let alone parse a
  // teaching note.
  React.useEffect(() => {
    if (!isActive || !submitted) return undefined;
    // Give the user more time to read when there's something to read.
    // Wrong-answer reveal (showing the right choice flagged green)
    // counts as something to read even without a written explanation.
    const delayMs = revealedExplanation
      ? POST_ANSWER_AUTO_ADVANCE_MS + 4000
      : isCorrect === false
      ? POST_ANSWER_AUTO_ADVANCE_MS + 2000
      : POST_ANSWER_AUTO_ADVANCE_MS;
    const t = setTimeout(() => {
      onAdvance && onAdvance();
    }, delayMs);
    return () => clearTimeout(t);
  }, [isActive, submitted, onAdvance, revealedExplanation, isCorrect]);

  const toggleChoice = (idx) => {
    if (submitted || submitting) return;
    setSelectedIndices((prev) => {
      if (isMultiSelect) {
        const next = new Set(prev);
        if (next.has(idx)) next.delete(idx);
        else next.add(idx);
        return next;
      }
      return new Set([idx]);
    });
  };

  const handleSubmit = async () => {
    if (submitted || submitting || selectedIndices.size === 0) return;
    setSubmitting(true);
    const indicesArr = Array.from(selectedIndices).sort((a, b) => a - b);
    try {
      const response = await onSubmitResponse(indicesArr);
      const correct = !!response?.is_correct;
      setIsCorrect(correct);
      setSubmitted(true);
      if (correct) {
        setFeedbackCopy(getPositiveCopy(language));
        vibrate([15, 40, 15]);
      } else {
        setFeedbackCopy(getNotQuiteCopy(language));
        setShake(true);
        vibrate(40);
        if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
        shakeTimerRef.current = setTimeout(() => setShake(false), 450);
      }
    } catch (_) {
      // Network failure — surface a toast (the cards' catch had been
      // silent, leaving the user wondering why their tap didn't take)
      // and allow retry on next click. State already rolled back via
      // the early-return guards on `submitted` / `submitting`.
      const tLocal = (en, es, zh) =>
        language === "spanish" ? es : language === "chinese" ? zh : en;
      toast.error(
        tLocal(
          "Couldn't submit your answer. Please try again.",
          "No se pudo enviar tu respuesta. Inténtalo de nuevo.",
          "无法提交答案。请再试一次。",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitted && selectedIndices.size > 0 && !submitting;

  const submitLabel = (() => {
    const lang = (language || "").toLowerCase();
    if (lang === "spanish") return "Enviar";
    if (lang === "chinese") return "提交";
    return "Submit";
  })();

  return (
    <motion.div
      ref={cardRef}
      animate={
        shake && !reducedMotion
          ? { x: [0, -8, 8, -6, 6, -3, 3, 0] }
          : { x: 0 }
      }
      transition={{ duration: 0.45 }}
      className="relative w-full h-full bg-white flex flex-col select-none"
    >
      {questionImageUrl && (
        <div
          className="relative bg-slate-200 overflow-hidden transition-[flex-basis] duration-300"
          style={{ flexBasis: `${band * 100}%`, flexGrow: 0, flexShrink: 1, minHeight: 0 }}
        >
          {/* Skeleton while the photo decodes — no black flash / hard pop-in;
              the image fades in over it on load (mirrors the split card). */}
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 animate-pulse">
              <ImageOutlinedIcon className="text-slate-400" style={{ fontSize: 40 }} />
            </div>
          )}
          <img
            ref={imgRef}
            src={questionImageUrl}
            alt=""
            className="w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: loaded ? 1 : 0 }}
            draggable={false}
            loading="eager"
            decoding="async"
            onLoad={handleLoad}
          />
        </div>
      )}

      {/* Content-first: this area takes the height it needs and the image
          band above yields space to it (the image can shrink below its
          aspect height on a crowded card). No internal scroll — scrolling
          here would swallow the deck's swipe gesture — so the interactive
          content (length-capped question + choices + Submit) is sized to
          always fit; the only unbounded piece, the explanation, gets the
          expand sheet instead.
          Top padding clears the player's top chrome (~52px: progress bar +
          close X) only when there's no image — `pt-14` (56px, 4px to spare).
          With an image band above, the image already clears the chrome, so
          the content sits tighter (`pt-6`). */}
      <div
        ref={contentRef}
        // Root font size for the scaled content. var(--fit-scale) is driven by
        // useFitToHeight (1 = unchanged). The question/choices size in em off
        // this, so a sub-1 scale reflows them to fit; at scale 1 it's 1rem and
        // the layout is pixel-identical to before.
        style={{ fontSize: "calc(1rem * var(--fit-scale, 1))" }}
        className={`grow shrink-0 flex flex-col px-6 ${
          questionImageUrl ? "pt-6" : "pt-14"
        } pb-6`}
      >
        <h2
          className="font-bold text-gray-900 leading-tight"
          // 1.25em = text-xl at scale 1; mb-6 (1.5rem) as 1.5em so the spacing
          // also tightens as the card shrinks.
          style={{ fontSize: "1.25em", marginBottom: "1.5em" }}
        >
          {question}
        </h2>

        <div className="flex flex-col gap-2">
          {displayOrder.map((idx) => {
            // `idx` is the *original* index into `choices` — we iterate
            // displayOrder so the visual position is shuffled, but every
            // selection / scoring / flash check below still keys off the
            // original idx, which is what the BE grades against.
            const choice = choices[idx];
            const isSelected = selectedIndices.has(idx);
            const showCorrectFlash =
              submitted && isSelected && isCorrect;
            const showWrongFlash =
              submitted && isSelected && isCorrect === false;
            // Reveal: when the user got the question wrong, surface the
            // correct choice(s) flagged green so they actually learn
            // what the right answer was. Only triggers when the BE
            // returned correct_indices on the response (or preview
            // synthesized them); without that we'd be flagging based
            // on the user's original answer-stripped payload, which
            // doesn't have the data to flag with.
            const isRevealedCorrect =
              submitted &&
              isCorrect === false &&
              revealedCorrectIndices &&
              revealedCorrectIndices.has(idx) &&
              !isSelected;

            // Tinted-chip treatment matching CompleteQuizModal so quizzes
            // look the same wherever they appear. The tint carries the
            // state (slate idle / violet selected / emerald correct /
            // rose wrong); the small left icon reinforces it.
            let bgClass = "bg-slate-50";
            let textClass = "text-slate-700";
            let borderClass = "border-slate-200";
            let iconWrapClass = "border-2 border-slate-300 bg-white";
            let iconNode = null;
            if (showCorrectFlash || isRevealedCorrect) {
              bgClass = "bg-emerald-50";
              textClass = "text-emerald-900";
              borderClass = "border-emerald-300";
              iconWrapClass = "bg-emerald-600";
              iconNode = (
                <CheckIcon style={{ fontSize: 14, color: "white" }} />
              );
            } else if (showWrongFlash) {
              bgClass = "bg-rose-50";
              textClass = "text-rose-900";
              borderClass = "border-rose-300";
              iconWrapClass = "bg-rose-600";
              iconNode = (
                <CloseIcon style={{ fontSize: 14, color: "white" }} />
              );
            } else if (isSelected) {
              bgClass = "bg-violet-50";
              textClass = "text-violet-900";
              borderClass = "border-violet-300";
              iconWrapClass = "bg-violet-600";
              iconNode = (
                <CheckIcon style={{ fontSize: 14, color: "white" }} />
              );
            }

            return (
              <motion.div
                key={idx}
                role={interactive ? "button" : undefined}
                tabIndex={interactive && !submitted && !submitting ? 0 : -1}
                aria-disabled={!interactive || submitted || submitting}
                aria-pressed={interactive ? isSelected : undefined}
                onClick={() => {
                  if (interactive && !submitted && !submitting) toggleChoice(idx);
                }}
                onKeyDown={(e) => {
                  if (
                    interactive &&
                    (e.key === "Enter" || e.key === " ") &&
                    !submitted &&
                    !submitting
                  ) {
                    e.preventDefault();
                    toggleChoice(idx);
                  }
                }}
                // A div (role="button"), not a native <button>: in this Embla
                // swipe deck the drag initiates from plain elements (like the
                // image) but NOT from native buttons, so the choices were a
                // dead zone for swiping. A div drags through like the image
                // while keeping click + keyboard activation. (whileTap is also
                // omitted — framer's press gesture would re-capture the
                // pointer and swallow the drag.)
                animate={
                  (showCorrectFlash || isRevealedCorrect) && !reducedMotion
                    ? { scale: [1, 1.03, 1] }
                    : { scale: 1 }
                }
                transition={{ duration: 0.25 }}
                className={[
                  "relative w-full text-left rounded-lg p-3 border flex items-start gap-3 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-violet-500",
                  interactive && !submitted && !submitting ? "cursor-pointer" : "",
                  bgClass,
                  borderClass,
                  // Dim unselected wrong choices post-submit so the
                  // user's pick (or the revealed-correct chip) reads as
                  // primary — but never dim a revealed-correct chip
                  // since that's the whole point of the reveal.
                  submitted && !isSelected && !isRevealedCorrect
                    ? "opacity-60"
                    : "",
                ].join(" ")}
              >
                <motion.div
                  initial={false}
                  animate={
                    (showCorrectFlash || showWrongFlash || isRevealedCorrect) &&
                    !reducedMotion
                      ? { scale: [0, 1.15, 1] }
                      : { scale: 1 }
                  }
                  transition={{
                    duration: 0.3,
                    type: reducedMotion ? "tween" : "spring",
                    stiffness: 500,
                    damping: 22,
                  }}
                  className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center ${iconWrapClass}`}
                >
                  {iconNode}
                </motion.div>
                <span
                  // No explicit text size: inherits the scaled root font (1rem
                  // at scale 1 = the old text-base), so choices shrink to fit.
                  className={`flex-1 min-w-0 break-words font-medium ${textClass}`}
                >
                  {choice?.text || ""}
                </span>
              </motion.div>
            );
          })}
        </div>

        {/* Submit button (hidden once answered). In the display-only preview
            it still renders looking fully enabled — so the tile matches the
            real card — but is inert (no handler, pointer-events off). */}
        {!submitted && (
          <button
            type="button"
            onClick={interactive ? handleSubmit : undefined}
            disabled={interactive && !canSubmit}
            tabIndex={interactive ? undefined : -1}
            className={[
              // text-base pins the button to 1rem so it stays readable/tappable
              // even when the question + choices above are scaled down to fit.
              "mt-6 w-full rounded-xl py-3 text-base text-white font-semibold transition-opacity",
              !interactive || canSubmit ? "opacity-100" : "opacity-50",
              interactive ? "" : "pointer-events-none",
            ].join(" ")}
            style={{ backgroundColor: BRAND_VIOLET }}
          >
            {submitting ? "…" : submitLabel}
          </button>
        )}

        {/* Post-answer feedback copy */}
        <AnimatePresence>
          {submitted && feedbackCopy && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.3 }}
              className={[
                "mt-6 text-center text-base font-semibold",
                isCorrect ? "text-green-700" : "text-red-700",
              ].join(" ")}
            >
              {feedbackCopy}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Explanation — shown after answering, regardless of right/
            wrong. The BE strips it from the pre-submit card payload
            (security) and returns it on the response payload, so this
            renders correctly in both preview and employee modes.
            Clamped so a long explanation can't grow the card past a clip;
            when it overflows the clamp, a "… more" opens the full text in
            the expand sheet (portaled out of the swipe area). */}
        <AnimatePresence>
          {submitted && revealedExplanation && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : 0.3, delay: 0.1 }}
              className="mt-4 rounded-lg bg-slate-100 overflow-hidden"
            >
              <div
                ref={explanationRef}
                className="relative max-h-28 overflow-hidden p-3 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap"
              >
                {revealedExplanation}
                {explanationOverflowing && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                    onClick={openExplanation}
                    className="absolute bottom-0 left-0 right-0 flex justify-start px-3 pt-8 pb-2 bg-gradient-to-t from-slate-100 via-slate-100 to-transparent"
                  >
                    <span className="text-xs font-semibold text-violet-700">
                      … more
                    </span>
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <CardExpandSheet
        open={explanationExpanded}
        onClose={() => setExplanationExpanded(false)}
        title={question}
        scrollRef={explanationSheetRef}
      >
        {revealedExplanation}
      </CardExpandSheet>
    </motion.div>
  );
}
